import TreeSelectionController from "../selectionTree/TreeSelectionController";
import TreeNodeSet from "./TreeNodeSet";

export default class TriStateSelectionController extends TreeSelectionController {
    constructor(tree, alwaysSelectedNodes = []) {
        super(tree);

        this.alwaysSelectedNodes = alwaysSelectedNodes
        this.alwaysSelectedNodesSet = new TreeNodeSet()
        alwaysSelectedNodes.forEach(node => this.alwaysSelectedNodesSet.add(node))

        this.intermediateSet = new TreeNodeSet();
        this.listeners = [];
    }

    markSelected(treeNode) {
        if (!this.canSelectNode(treeNode)) {
            return;
        }

        super.markSelected(treeNode);
        this.intermediateSet.remove(treeNode);
    }

    markDeselected(treeNode) {
        if (!this.canDeselectNode(treeNode)) {
            return;
        }

        super.markDeselected(treeNode);
        this.intermediateSet.remove(treeNode);
    }

    nodeSelected(treeNode) {
        if (!this.canSelectNode(treeNode)) {
            return;
        }

        this.selectSubtree(treeNode, true);
        this.fireNodeSelected(treeNode);
    }

    nodeDeselected(treeNode) {
        if (!this.canDeselectNode(treeNode)) {
            return;
        }

        this.deselectSubtree(treeNode);
        this.fireNodeDeselected(treeNode);
    }

    selectSubtree(treeNode, updateUIofNodes) {
        if (!this.canSelectNode(treeNode)) {
            return;
        }

        this.markSelected(treeNode);

        if (updateUIofNodes)
            this.tree.updateUIForSingleTreeNode(treeNode);

        this.selectChildrenInDepth(treeNode, updateUIofNodes);

        // set intermediate selection state for all parent nodes
        const maxTopLevel = this.tree.isRootVisible() ? 0 : 1;
        let parentNode = treeNode.getParent();
        while (parentNode.getLevel() >= maxTopLevel) {
            this.updateParentNodeStateOnSubtreeSelected(parentNode);
            if (updateUIofNodes)
                this.tree.updateUIForSingleTreeNode(parentNode);
            parentNode = parentNode.getParent();
        }
    }

    updateParentNodeStateOnSubtreeSelected(parentNode) {
        this.intermediateSet.add(parentNode);
    }

    markDeselectedAndUpdate(treeNode) {
        if (!this.canDeselectNode(treeNode)) {
            return;
        }

        this.markDeselected(treeNode);
        this.tree.updateUIForSingleTreeNode(treeNode);
    }

    deselectSubtree(treeNode) {
        if (!this.canDeselectNode(treeNode)) {
            return;
        }

        this.markDeselectedAndUpdate(treeNode);

        const children = treeNode.getChildren();
        for (let i = 0; i < children.length; i++)
            this.deselectSubtree(children[i]);

        const maxTopLevel = this.tree.isRootVisible() ? 0 : 1;
        treeNode = treeNode.getParent();
        let foundIntermediate = false;
        while (treeNode.getLevel() >= maxTopLevel) {
            super.markDeselected(treeNode);

            if (foundIntermediate || this.hasAnySelectedChild(treeNode)) {
                this.intermediateSet.add(treeNode);
                foundIntermediate = true;
            }
            else {
                this.intermediateSet.remove(treeNode);
            }

            this.tree.updateUIForSingleTreeNode(treeNode);
            treeNode = treeNode.getParent();
        }
    }

    hasAnySelectedChild(treeNode) {
        const children = treeNode.getChildren();
        for (let i = 0; i < children.length; i++) {
            const childNode = children[i];
            if (this.isSelected(childNode) || this.intermediateSet.contains(childNode))
                return true;
        }
        return false;
    }

    hasAllChildrenSelected(treeNode) {
        const children = treeNode.getChildren();
        for (let i = 0; i < children.length; i++) {
            const childNode = children[i];
            if (!this.isSelected(childNode))
                return false;
        }
        return true;
    }

    hasAllChildrenDisabled(treeNode) {
        const children = treeNode.getChildren();
        for (let i = 0; i < children.length; i++) {
            const childNode = children[i];
            if (this.canDeselectNode(childNode))
                return false;
        }
        return true;
    }

    deselectAll() {
        const newSelection = new TreeNodeSet()
        const newIntermediate = new TreeNodeSet()

        this.alwaysSelectedNodes.forEach(node => {
            // Always selected nodes will appear in new selection
            newSelection.add(node)

            // Parents of always selected nodes should be checked as intermediate
            // unless they are also marked as always selected
            let parent = node.getParent()
            while (parent) {
                if (!this.alwaysSelectedNodesSet.contains(parent)) {
                    newIntermediate.add(parent)
                }
                parent = parent.getParent()
            }
        })

        const oldSelection = this.nodeSet;
        this.nodeSet = newSelection;
        this.tree.updateUIForTreeNodeSet(oldSelection);

        const oldIntermediate = this.intermediateSet;
        this.intermediateSet = newIntermediate;
        this.tree.updateUIForTreeNodeSet(oldIntermediate);
    }

    containsSelectedChildren(treeNode) {
        return this.intermediateSet.contains(treeNode);
    }

    addListener(listener) {
        this.listeners.push(listener);
    }

    fireNodeSelected(treeNode) {
        for (let i = 0; i < this.listeners.length; i++)
            this.listeners[i].nodeSelected(treeNode);
    }

    fireNodeDeselected(treeNode) {
        for (let i = 0; i < this.listeners.length; i++)
            this.listeners[i].nodeDeselected(treeNode);
    }

    updateUI() {
        super.updateUI();
        this.tree.updateUIForTreeNodeSet(this.intermediateSet);
    }

    selectChildrenInDepth(treeNode, updateUIofNodes) {
        const children = treeNode.getChildren();
        for (let i = 0; i < children.length; i++)
            this.selectSubtree(children[i], updateUIofNodes);
    }

    deselectChildrenInDepth(treeNode, updateUIofNodes) {
        const children = treeNode.getChildren();
        for (let i = 0; i < children.length; i++)
            this.deselectSubtree(children[i], updateUIofNodes);
    }

    resetSelectedNodes(nodeSet) {
        if (!nodeSet) {
            nodeSet = this.initiallySelectedNodeSet;
        }
        this.nodeSet = nodeSet.clone();

        const allLevels = nodeSet.getAllLevels();

        //todo optimise (it reselects nodes that were already selected due-to parent's selection)
        for (let i = 0; i < allLevels.length; i++) {
            //noinspection JSUnfilteredForInLoop
            const levelMap = nodeSet.getLevelMap(allLevels[i]);
            for (let j in levelMap)
                    //noinspection JSUnfilteredForInLoop
                if (this.nodeSet.contains(levelMap[j]))
                //noinspection JSUnfilteredForInLoop
                    this.selectSubtree(levelMap[j], false);
        }

    }

    canSelectNode(treeNode) {
        return true;
    }

    canDeselectNode(treeNode) {
        return !this.alwaysSelectedNodesSet.contains(treeNode);
    }
}



// WEBPACK FOOTER //
// ./web/js/hierarchySelector/tree/TriStateSelectionController.js